import { FC, useEffect, useRef, useState } from 'react';

import { Button, Box, Tooltip, useTheme } from '@mui/material';

import { MasterIndexApi } from '@/api';
import { IcHistoryGray } from '@/assets/images';
import { AppModal } from '@/components';
import { useCFSContext } from '@/hooks';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';

import CFSHistoryDataGrid from './components/CFSHistoryDataGrid';

interface CFSHistoryModalProps {
  addressId?: string;
  currentCfsId?: string;
}

const CFSHistoryModal: FC<CFSHistoryModalProps> = ({
  addressId,
  currentCfsId,
}) => {
  const { updateToast } = useToastStore();
  const { cfs } = useCFSContext();
  const theme = useTheme();
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const hasFetchedRef = useRef(false);

  const effectiveAddressId = addressId || cfs?.addressInfo?.address?._id;
  const effectiveCfsId = currentCfsId || cfs?._id;

  const fetchCFSCount = async () => {
    if (!effectiveAddressId) return;
    try {
      const response = await MasterIndexApi.getAddressRelatedRecords(
        effectiveAddressId,
        {
          limit: 1000,
          sort: JSON.stringify({ createdAt: -1 }),
        },
      );

      let records = [];

      if (Array.isArray(response.data)) {
        records = response.data;
      } else if (response.data.docs) {
        records = response.data.docs;
      } else if (response.data.results) {
        records = response.data.results;
      } else {
        setTotalCount(0);
        return;
      }

      const cfsRecords = records.filter(
        (record: any) =>
          record.name &&
          record.type &&
          record.type === 'CFS' &&
          record._id !== effectiveCfsId,
      );

      setTotalCount(cfsRecords.length);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  // Reset count when address changes so stale data is not shown
  useEffect(() => {
    setTotalCount(null);
    hasFetchedRef.current = false;
  }, [effectiveAddressId]);

  const onClose = () => {
    setOpenModal(false);
  };

  const handleOpen = () => {
    setOpenModal(true);
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchCFSCount();
    }
  };

  // Don't show the button if no address is selected
  if (!effectiveAddressId) {
    return null;
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Tooltip title="Address History">
        <Button
          color="primary"
          onClick={handleOpen}
          sx={{
            height: '36px',
            minWidth: '36px',
            padding: 0,
            borderRadius: '8px',
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : colors.grey[100],
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? '#223640' : '#F5F7F7',
            },
          }}
          aria-label="Address History"
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.formSubHeader
                    : colors.grey[20],
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.text.primary
                    : colors.grey[100],
                borderRadius: '6px',
                padding: '0 6px',
                width: '44px',
                minWidth: '44px',
                height: '24px',
                boxSizing: 'border-box',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: '4px',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IcHistoryGray width={16} height={16} />
              </Box>
              {totalCount !== null && totalCount > 0 && (
                <Box component="span" sx={{ fontSize: '0.7rem' }}>
                  {totalCount}
                </Box>
              )}
            </Box>
          </Box>
        </Button>
      </Tooltip>
      <AppModal open={openModal} title="Address history" onClose={onClose}>
        <CFSHistoryDataGrid
          addressId={effectiveAddressId}
          currentCfsId={effectiveCfsId}
        />
      </AppModal>
    </Box>
  );
};

export default CFSHistoryModal;
